import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Gift, Loader2, X } from 'lucide-react';
import { giftPoints, type GiftPointsResult } from '@/lib/api';
import { useUserSummary } from '@/hooks/useGamification';
import styles from './GiftPointsSheet.module.css';

/**
 * Bottom-sheet gift flow — send points to a confirmed friend.
 *
 * Mirrors RedemptionSheet's shape: portal to document.body (the
 * friends routes live inside AppShell's `.pageTransition` transform
 * wrapper, which promotes itself to the containing block for
 * position:fixed descendants — without the portal the sheet anchors
 * to the wrapper and renders off-position, the exact trap
 * RedemptionSheet documents), a mount-stable Idempotency-Key re-sent
 * on every confirm so a flaky retry collapses to one gift
 * server-side, and the same amount-selection pattern bounded by the
 * giver's spendable balance.
 *
 * The per-recipient monthly cap is enforced server-side only — no
 * pre-fetch; a cap rejection arrives with the remaining headroom in
 * the hint and is surfaced verbatim. An `idempotent: true` response
 * is a success (the retry was deduplicated), never an error.
 *
 * No confetti by design: gifting is a transfer between friends, not
 * a milestone — a clean success card carries the moment.
 */

interface GiftPointsSheetProps {
  recipientUserId: string;
  recipientName: string;
  onClose: () => void;
}

const QUICK_AMOUNTS = [50, 100, 250, 500];

export function GiftPointsSheet({
  recipientUserId, recipientName, onClose,
}: GiftPointsSheetProps) {
  const { t } = useTranslation('friendDetail');
  const qc = useQueryClient();
  // The giver's spendable balance — the same summary read every other
  // gamification surface uses (30s staleTime, shared cache).
  const summaryQ = useUserSummary();
  const balance = Math.floor(summaryQ.data?.totalPoints ?? 0);

  const [points, setPoints] = useState(0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<GiftPointsResult | null>(null);

  // Idempotency token — stable for the lifetime of this sheet so a
  // confirm-retry after a network blip dedupes server-side (and a
  // retry after a half-applied pair repairs it).
  const idemRef = useRef<string>(
    `gift-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  );

  // Initialise the amount once the balance is known.
  useEffect(() => {
    if (balance > 0 && points === 0) {
      setPoints(Math.min(50, balance));
    }
  }, [balance, points]);

  const quickAmounts = useMemo(
    () => QUICK_AMOUNTS.filter((n) => n <= balance),
    [balance],
  );
  const valid = points >= 1 && points <= balance;

  const handleConfirm = async () => {
    if (!valid || pending) return;
    setPending(true);
    setError(null);
    try {
      const res = await giftPoints(
        { recipientUserId, points },
        idemRef.current,
      );
      setSuccess(res);
      // Refresh the giver's balance everywhere (rewards hero, profile
      // points card, this sheet's own summary read).
      qc.invalidateQueries({ queryKey: ['gamification'] });
    } catch (err: unknown) {
      // ky throws HTTPError on non-2xx; surface the server's message
      // and hint verbatim (cap headroom, insufficient balance, not a
      // friend) — the same parse RedemptionSheet uses.
      const httpErr = err as { response?: Response; message?: string };
      let msg = httpErr.message ?? t('gift.errGiftFailed');
      if (httpErr.response) {
        try {
          const j = await httpErr.response.clone().json() as
            { error?: { message?: string } | string; hint?: string };
          msg = typeof j.error === 'string' ? j.error : (j.error?.message ?? msg);
          if (j.hint) msg += ` ${j.hint}`;
        } catch { /* fall through */ }
      }
      setError(msg);
    } finally {
      setPending(false);
    }
  };

  // SSR / pre-hydration guard — keeps the component portable even
  // though Vite builds client-only today.
  if (typeof document === 'undefined') return null;

  const sheetTree = (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={t('gift.sheetAria', { name: recipientName })}
      onClick={onClose}
    >
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.handle} aria-hidden />
        <div className={styles.header}>
          <h2 className={styles.title}>{t('gift.title')}</h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('gift.close')}
          >
            <X size={16} />
          </button>
        </div>

        {success ? (
          <div className={styles.successBlock}>
            <div className={styles.successIcon}>
              <Gift size={40} />
            </div>
            <h3 className={styles.successTitle}>
              {t('gift.success.sent', {
                count: success.pointsGifted,
                points: success.pointsGifted.toLocaleString(),
              })}
            </h3>
            <p className={styles.successDesc}>
              {t('gift.success.to', { name: success.recipientUsername || recipientName })}
              {success.idempotent && t('gift.success.deduped')}
            </p>
            {success.giverBalance != null && (
              <p className={styles.successBalance}>
                {t('gift.success.balance', { balance: success.giverBalance.toLocaleString() })}
              </p>
            )}
            <button type="button" className={styles.doneBtn} onClick={onClose}>
              {t('gift.done')}
            </button>
          </div>
        ) : (
          <>
            <div className={styles.balanceBlock}>
              <span className={styles.balanceLabel}>{t('gift.available')}</span>
              <span className={styles.balanceValue}>
                {balance.toLocaleString()}
              </span>
              <span className={styles.balanceUnit}>{t('gift.pointsUnit')}</span>
              <span className={styles.recipientLine}>
                {t('gift.recipientTo')} <strong>{recipientName}</strong>
              </span>
            </div>

            {quickAmounts.length > 0 && (
              <div className={styles.quickRow}>
                {quickAmounts.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPoints(n)}
                    className={`${styles.quickBtn} ${points === n ? styles.quickBtnActive : ''}`}
                  >
                    {n.toLocaleString()}
                  </button>
                ))}
              </div>
            )}

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('gift.amount')}</span>
              <input
                type="number"
                inputMode="numeric"
                className={styles.amountInput}
                value={points}
                min={1}
                max={balance}
                onChange={(e) => {
                  const n = Math.max(0, Math.min(balance, Math.floor(Number(e.target.value) || 0)));
                  setPoints(n);
                }}
              />
            </label>

            {error && <div className={styles.error}>{error}</div>}

            <button
              type="button"
              className={styles.confirmBtn}
              onClick={handleConfirm}
              disabled={!valid || pending}
            >
              {pending
                ? <><Loader2 size={14} className={styles.spinner} /> {t('gift.gifting')}</>
                : t('gift.giftAmount', { points: points.toLocaleString() })}
            </button>
            <p className={styles.smallprint}>
              {t('gift.smallprint')}
            </p>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(sheetTree, document.body);
}
